import React, { useEffect, useState } from 'react';
import { Button, ButtonGroup, Card, Col, Form, Row } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  ActivationStatisticsLevel,
  getActivationStatistics,
  IActivationLevelStatistics,
  IActivationSegmentStatistics,
  IActivationStatistics
} from '../../../Services/License';
import { asyncErrorHandler } from '@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler';
import { handleError } from '@abstract/abstractwebcommon-client/ErrorHandler/ErrorHandler';
import './ActivationStatistics.css';

/** Range options exposed in the dropdown. `value` is what we send to the API. */
const RANGE_OPTIONS: { labelKey: string; value: number | 'all' }[] = [
  { labelKey: 'admin.page.dashboard.activation.range.7d', value: 7 },
  { labelKey: 'admin.page.dashboard.activation.range.30d', value: 30 },
  { labelKey: 'admin.page.dashboard.activation.range.90d', value: 90 },
  { labelKey: 'admin.page.dashboard.activation.range.365d', value: 365 },
  { labelKey: 'admin.page.dashboard.activation.range.all', value: 'all' }
];

/** Format a bucket's `date` (start-of-period) for display under each bar. */
const formatBucketTick = (
  bucketing: 'day' | 'week' | 'month',
  isoDate: string
): string => {
  const date: Date = parseISO(isoDate);
  if (bucketing === 'day') {
    return format(date, 'd');
  }
  if (bucketing === 'week') {
    return format(date, 'MMM d');
  }
  return format(date, 'MMM yy');
};

/** Tooltip header label for a bar. */
const formatBucketTooltip = (
  bucketing: 'day' | 'week' | 'month',
  isoDate: string
): string => {
  const date: Date = parseISO(isoDate);
  if (bucketing === 'day') {
    return format(date, 'MMM d');
  }
  if (bucketing === 'week') {
    return `Week of ${format(date, 'MMM d, yyyy')}`;
  }
  return format(date, 'MMM yyyy');
};

const Chart = ({
  bucketing,
  series,
  emptyLabel,
  newLabel,
  returningLabel
}: {
  bucketing: 'day' | 'week' | 'month';
  series: { date: string; newCount: number; returningCount: number }[];
  emptyLabel: string;
  newLabel: string;
  returningLabel: string;
}): JSX.Element => {
  if (!series.length) {
    return <div className="activation-chart-empty">{emptyLabel}</div>;
  }
  // Bars are sized against the tallest stacked total so the whole chart fits.
  const max: number = Math.max(
    ...series.map((bucket) => bucket.newCount + bucket.returningCount),
    1
  );
  return (
    <>
      <div className="activation-chart">
        {series.map((bucket) => {
          const total: number = bucket.newCount + bucket.returningCount;
          return (
            <div className="activation-chart-day" key={bucket.date}>
              <div className="activation-chart-bar-column">
                <div
                  className="activation-chart-bar"
                  style={{
                    // Always keep a tiny nub on zero-count buckets so the axis
                    // reads cleanly.
                    height: `${Math.max((total / max) * 100, total ? 8 : 2)}%`
                  }}
                >
                  {/* Returning stacks on top of new (new anchored to the axis). */}
                  <div
                    className="activation-chart-bar-returning"
                    style={{ flexGrow: bucket.returningCount }}
                  />
                  <div
                    className="activation-chart-bar-new"
                    style={{ flexGrow: bucket.newCount }}
                  />
                  <div className="activation-chart-tooltip">
                    <strong>{total}</strong>
                    <small>{formatBucketTooltip(bucketing, bucket.date)}</small>
                    <small>
                      {newLabel}: {bucket.newCount}
                    </small>
                    <small>
                      {returningLabel}: {bucket.returningCount}
                    </small>
                  </div>
                </div>
              </div>
              <span>{formatBucketTick(bucketing, bucket.date)}</span>
            </div>
          );
        })}
      </div>
      <div className="activation-chart-legend">
        <span className="activation-chart-legend-item">
          <span className="activation-chart-legend-swatch activation-chart-bar-new" />
          {newLabel}
        </span>
        <span className="activation-chart-legend-item">
          <span className="activation-chart-legend-swatch activation-chart-bar-returning" />
          {returningLabel}
        </span>
      </div>
    </>
  );
};

const StatCard = ({
  title,
  segment,
  level,
  notConfiguredLabel,
  newInRangeLabel,
  returningInRangeLabel
}: {
  title: string;
  segment: IActivationSegmentStatistics;
  level: ActivationStatisticsLevel;
  notConfiguredLabel: string;
  newInRangeLabel: string;
  returningInRangeLabel: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const stats: IActivationLevelStatistics = segment[level];
  // Overall has no `configured` flag and always renders; pioneer/trial show the
  // "not configured" hint only while there is nothing to display.
  const showNotConfigured: boolean =
    segment.configured === false && stats.total === 0;
  return (
    <Card className="activation-card mb-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-baseline mb-3">
          <h5 className="mb-0">{title}</h5>
          {segment.projectName && (
            <small className="text-muted">{segment.projectName}</small>
          )}
        </div>
        {showNotConfigured ? (
          <div className="text-muted">{notConfiguredLabel}</div>
        ) : (
          <>
            <Row className="activation-stats">
              <Col xs={6} md={3}>
                <div className="activation-stat-value">{stats.newInRange}</div>
                <div className="activation-stat-label">{newInRangeLabel}</div>
              </Col>
              <Col xs={6} md={3}>
                <div className="activation-stat-value">
                  {stats.returningInRange}
                </div>
                <div className="activation-stat-label">
                  {returningInRangeLabel}
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="activation-stat-value">{stats.active}</div>
                <div className="activation-stat-label">
                  {t('admin.page.dashboard.activation.active')}
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div className="activation-stat-value">{stats.total}</div>
                <div className="activation-stat-label">
                  {t('admin.page.dashboard.activation.total')}
                </div>
              </Col>
            </Row>
            <Chart
              bucketing={stats.bucketing}
              series={stats.series}
              emptyLabel={t('admin.page.dashboard.activation.noData')}
              newLabel={t('admin.page.dashboard.activation.new')}
              returningLabel={t('admin.page.dashboard.activation.returning')}
            />
          </>
        )}
      </Card.Body>
    </Card>
  );
};

const ActivationStatistics = (): JSX.Element | null => {
  const { t } = useTranslation();
  const [data, setData] = useState<IActivationStatistics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rangeDays, setRangeDays] = useState<number | 'all'>(30);
  const [level, setLevel] = useState<ActivationStatisticsLevel>('user');

  useEffect(() => {
    let isCancelled = false;
    const fetch = async () => {
      setIsLoading(true);
      try {
        const response = await asyncErrorHandler(
          getActivationStatistics(rangeDays)
        );
        if (isCancelled) {
          return;
        }
        if (response?.error) {
          handleError({ message: response.error.message });
          return;
        }
        setData(response?.data ?? null);
      } catch (error: any) {
        if (!isCancelled) {
          handleError({ message: error?.message });
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    fetch();
    return () => {
      isCancelled = true;
    };
  }, [rangeDays]);

  if (isLoading && !data) {
    return null;
  }
  if (!data) {
    return null;
  }

  const notConfigured: string = t(
    'admin.page.dashboard.activation.notConfigured'
  );
  // The "in range" counters relabel to "all-time" so the cards stay readable.
  const isAllTime: boolean = rangeDays === 'all';
  const newInRangeLabel: string = isAllTime
    ? t('admin.page.dashboard.activation.newAllTime')
    : t('admin.page.dashboard.activation.newInRange');
  const returningInRangeLabel: string = isAllTime
    ? t('admin.page.dashboard.activation.returningAllTime')
    : t('admin.page.dashboard.activation.returningInRange');

  return (
    <div className="activation-statistics mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h4 className="mb-0">{t('admin.page.dashboard.activation.title')}</h4>
        <div className="d-flex align-items-center activation-controls">
          <ButtonGroup size="sm" className="mr-3">
            <Button
              variant={level === 'user' ? 'primary' : 'outline-primary'}
              onClick={() => setLevel('user')}
            >
              {t('admin.page.dashboard.activation.byUser')}
            </Button>
            <Button
              variant={level === 'machine' ? 'primary' : 'outline-primary'}
              onClick={() => setLevel('machine')}
            >
              {t('admin.page.dashboard.activation.byMachine')}
            </Button>
          </ButtonGroup>
          <Form.Group className="activation-range mb-0">
            <Form.Label className="mr-2 mb-0">
              {t('admin.page.dashboard.activation.range.label')}
            </Form.Label>
            <Form.Control
              as="select"
              size="sm"
              value={String(rangeDays)}
              onChange={(event) => {
                const next: string = event.target.value;
                setRangeDays(next === 'all' ? 'all' : Number(next));
              }}
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={String(option.value)} value={String(option.value)}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </div>
      </div>
      <Row>
        <Col xs={12}>
          <StatCard
            title={t('admin.page.dashboard.activation.overall')}
            segment={data.overall}
            level={level}
            notConfiguredLabel={notConfigured}
            newInRangeLabel={newInRangeLabel}
            returningInRangeLabel={returningInRangeLabel}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} lg={4}>
          <StatCard
            title={t('admin.page.dashboard.activation.pioneer')}
            segment={data.pioneer}
            level={level}
            notConfiguredLabel={notConfigured}
            newInRangeLabel={newInRangeLabel}
            returningInRangeLabel={returningInRangeLabel}
          />
        </Col>
        <Col md={12} lg={4}>
          <StatCard
            title={t('admin.page.dashboard.activation.trial')}
            segment={data.trial}
            level={level}
            notConfiguredLabel={notConfigured}
            newInRangeLabel={newInRangeLabel}
            returningInRangeLabel={returningInRangeLabel}
          />
        </Col>
        <Col md={12} lg={4}>
          <StatCard
            title={t('admin.page.dashboard.activation.paid')}
            segment={data.paid}
            level={level}
            notConfiguredLabel={notConfigured}
            newInRangeLabel={newInRangeLabel}
            returningInRangeLabel={returningInRangeLabel}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ActivationStatistics;
